import { useAtom } from 'jotai';
import { CheckCircle, Clock, RefreshCw, Search, XCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  activeJobsAtom,
  clearTransferJobsAtom,
  completedJobsAtom,
  failedJobsAtom,
  sortedJobsAtom,
} from '@/atoms/transfer-jobs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { TransferJobFilters } from '@/types/transfer-job';
import { TransferJobCard } from './transfer-job-card';
import { TransferJobStatusUpdater } from './transfer-job-status-updater';

interface TransferHistoryProps {
  className?: string;
}

export function TransferHistory({ className }: TransferHistoryProps) {
  const { t } = useTranslation();
  const [jobs] = useAtom(sortedJobsAtom);
  const [activeJobs] = useAtom(activeJobsAtom);
  const [completedJobs] = useAtom(completedJobsAtom);
  const [failedJobs] = useAtom(failedJobsAtom);
  const [, clearJobs] = useAtom(clearTransferJobsAtom);

  const [_filters, _setFilters] = useState<TransferJobFilters>({});
  const [searchQuery, setSearchQuery] = useState('');

  const handleClearHistory = () => {
    if (confirm(t('confirmations.clearHistoryConfirm'))) {
      clearJobs();
    }
  };

  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      const matchesSearch =
        !searchQuery ||
        job.jobId.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.transferId.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.fromAddress.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.toAddress.toLowerCase().includes(searchQuery.toLowerCase());

      return matchesSearch;
    });
  }, [jobs, searchQuery]);

  return (
    <div className={cn(className, 'space-y-4')}>
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">{t('transfer.transferBridgeHistory')}</h2>
        {jobs.length > 0 && (
          <Button variant="outline" size="sm" onClick={handleClearHistory}>
            {t('common.clearHistory')}
          </Button>
        )}
      </div>

      <p className="text-sm text-muted-foreground">
        {t('transfer.allRecordsStoredLocally')}
      </p>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder={t('transfer.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>
      <Tabs defaultValue="all" className="space-y-4">
        <TabsList className='w-full'>
          <TabsTrigger
            value="all"
            className="flex items-center justify-center gap-1 px-1.5 text-xs md:text-sm md:gap-2 md:px-2"
          >
            {t('common.all')}
            <Badge
              variant="secondary"
              className="ml-0.5 text-[10px] md:text-xs md:ml-1"
            >
              {jobs.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger
            value="active"
            className="flex items-center justify-center gap-1 px-1.5 text-xs md:text-sm md:gap-2 md:px-2"
          >
            <RefreshCw className="h-3 w-3 hidden md:inline" />
            {t('common.active')}
            <Badge
              variant="secondary"
              className="ml-0.5 text-[10px] md:text-xs md:ml-1"
            >
              {activeJobs.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger
            value="completed"
            className="flex items-center justify-center gap-1 px-1.5 text-xs md:text-sm md:gap-2 md:px-2"
          >
            <CheckCircle className="h-3 w-3 hidden md:inline" />
            {t('common.completed')}
            <Badge
              variant="secondary"
              className="ml-0.5 text-[10px] md:text-xs md:ml-1"
            >
              {completedJobs.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger
            value="failed"
            className="flex items-center justify-center gap-1 px-1.5 text-xs md:text-sm md:gap-2 md:px-2"
          >
            <XCircle className="h-3 w-3 hidden md:inline" />
            {t('common.failed')}
            <Badge
              variant="secondary"
              className="ml-0.5 text-[10px] md:text-xs md:ml-1"
            >
              {failedJobs.length}
            </Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          {filteredJobs.length > 0 ? (
            filteredJobs.map((job) => (
              <TransferJobCard key={job.jobId} job={job} />
            ))
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t('transfer.noTransferHistoryFound')}</p>
              {searchQuery && (
                <p className="text-sm mt-2">
                  {t('transfer.tryAdjustingSearch')}
                </p>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="active" className="space-y-4">
          {activeJobs.length > 0 ? (
            activeJobs.map((job) => (
              <TransferJobCard key={job.jobId} job={job} />
            ))
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <RefreshCw className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t('transfer.noActiveTransfers')}</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="completed" className="space-y-4">
          {completedJobs.length > 0 ? (
            completedJobs.map((job) => (
              <TransferJobCard key={job.jobId} job={job} />
            ))
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t('transfer.noCompletedTransfers')}</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="failed" className="space-y-4">
          {failedJobs.length > 0 ? (
            failedJobs.map((job) => (
              <TransferJobCard key={job.jobId} job={job} />
            ))
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <XCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t('transfer.noFailedTransfers')}</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add status updaters for all active jobs */}
      {activeJobs.map((job) => (
        <TransferJobStatusUpdater key={job.jobId} jobId={job.jobId} />
      ))}
    </div>
  );
}
